import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Handshake, Plus, Trash, FloppyDisk, PencilSimple,
  Eye, EyeSlash, UploadSimple, ArrowSquareOut,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolveLogoUrl(logo_url) {
  if (!logo_url) return "";
  if (logo_url.startsWith("http://") || logo_url.startsWith("https://")) return logo_url;
  // Object-storage paths returned by the backend
  return `${ASSET_BASE}/${logo_url.replace(/^\/+/, "")}`;
}

function PartnerRow({ partner, onUpdate, onDelete, onUploadLogo }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: partner.name || "",
    website_url: partner.website_url || "",
    logo_url: partner.logo_url || "",
    description: partner.description || "",
    sort_order: partner.sort_order ?? 100,
    is_active: partner.is_active !== false,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    await onUpdate(partner.id, draft);
    setBusy(false);
    setEditing(false);
  };
  const toggleActive = async () => {
    setBusy(true);
    await onUpdate(partner.id, { is_active: !partner.is_active });
    setBusy(false);
  };

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-4 space-y-3"
         data-testid={`admin-partner-${partner.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-lg bg-stone-800 border border-stone-700/60 flex items-center justify-center overflow-hidden shrink-0">
          {partner.logo_url ? (
            <img src={resolveLogoUrl(partner.logo_url)} alt={partner.name}
                 className="max-h-14 max-w-14 object-contain" />
          ) : (
            <span className="text-stone-500 text-xs">no logo</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Organization name"
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500"
                data-testid={`partner-name-${partner.id}`} />
              <input value={draft.website_url} onChange={(e) => setDraft({ ...draft, website_url: e.target.value })}
                placeholder="https://example.org"
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
                data-testid={`partner-url-${partner.id}`} />
              <input value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })}
                placeholder="https://example.org/logo.svg (or leave blank and upload below)"
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
                data-testid={`partner-logo-${partner.id}`} />
              <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Short description (optional)"
                rows={2}
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-stone-300 focus:outline-none focus:border-emerald-500"
                data-testid={`partner-desc-${partner.id}`} />
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-stone-500 uppercase tracking-wider">Sort</label>
                <input type="number" value={draft.sort_order}
                  onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-20 bg-stone-800 border border-stone-700 rounded-lg px-2 py-1 text-xs text-white" />
                <label className="text-[10px] text-stone-500 uppercase tracking-wider ml-3 flex items-center gap-1.5">
                  <input type="checkbox" checked={draft.is_active}
                    onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                  Active
                </label>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-heading font-semibold text-white truncate">{partner.name}</p>
                {!partner.is_active && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-700 text-stone-400 font-bold uppercase tracking-wider">
                    Hidden
                  </span>
                )}
              </div>
              <a href={partner.website_url} target="_blank" rel="noreferrer"
                 className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 mt-0.5">
                {partner.website_url} <ArrowSquareOut size={11} />
              </a>
              {partner.description && (
                <p className="text-xs text-stone-400 mt-1">{partner.description}</p>
              )}
              <p className="text-[10px] text-stone-600 mt-1">Sort: {partner.sort_order ?? 100}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-stretch shrink-0">
          {editing ? (
            <button onClick={save} disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid={`save-partner-${partner.id}`}>
              <FloppyDisk size={12} /> Save
            </button>
          ) : (
            <button onClick={() => setEditing(true)}
              className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
              data-testid={`edit-partner-${partner.id}`}>
              <PencilSimple size={12} /> Edit
            </button>
          )}
          <button onClick={toggleActive} disabled={busy}
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${partner.is_active ? "bg-amber-600/20 text-amber-300 hover:bg-amber-600/30" : "bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"}`}
            data-testid={`toggle-partner-${partner.id}`}>
            {partner.is_active ? <><EyeSlash size={12} /> Hide</> : <><Eye size={12} /> Show</>}
          </button>
          <label className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 cursor-pointer"
                 data-testid={`upload-partner-${partner.id}`}>
            <UploadSimple size={12} /> Upload
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => e.target.files?.[0] && onUploadLogo(partner.id, e.target.files[0])} />
          </label>
          <button onClick={() => window.confirm(`Delete "${partner.name}"?`) && onDelete(partner.id)}
            className="bg-red-600/20 hover:bg-red-600/30 text-red-300 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"
            data-testid={`delete-partner-${partner.id}`}>
            <Trash size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PartnersManager() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newP, setNewP] = useState({ name: "", website_url: "", logo_url: "", description: "", sort_order: 100, is_active: true });
  const [msg, setMsg] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/partners`, { withCredentials: true });
      setPartners(r.data?.partners || []);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const create = async () => {
    if (!newP.name.trim() || !newP.website_url.trim()) {
      setMsg("Name and website are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/partners`, newP, { withCredentials: true });
      setNewP({ name: "", website_url: "", logo_url: "", description: "", sort_order: 100, is_active: true });
      setAdding(false);
      setMsg("Partner added");
      fetchAll();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Create failed");
    }
  };

  const update = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/partners/${id}`, patch, { withCredentials: true });
      fetchAll();
    } catch (e) { setMsg(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/partners/${id}`, { withCredentials: true });
      fetchAll();
    } catch (e) { setMsg(e.response?.data?.detail || "Delete failed"); }
  };

  const uploadLogo = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/partners/${id}/logo`, fd, {
        withCredentials: true,
      });
      setMsg("Logo uploaded");
      fetchAll();
    } catch (e) { setMsg(e.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="space-y-5" data-testid="partners-manager">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <Handshake size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">Partner Organizations</h2>
          <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">
            Manage partner organizations shown on the home page. Each card has a logo, name, and website link.
            Logos can be a public URL (PNG / SVG / JPG) or uploaded directly. Lower sort numbers appear first.
          </p>
        </div>
        <button onClick={() => setAdding(!adding)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 shrink-0"
          data-testid="add-partner-btn">
          <Plus size={14} weight="bold" /> {adding ? "Cancel" : "Add Partner"}
        </button>
      </div>

      {msg && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-lg px-3 py-2 text-xs text-emerald-300"
             data-testid="partners-msg">
          {msg}
        </div>
      )}

      {adding && (
        <div className="bg-stone-900 rounded-xl border border-emerald-700/30 p-4 space-y-2">
          <input value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })}
            placeholder="Organization name (e.g. UN Environment Programme)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            data-testid="new-partner-name" />
          <input value={newP.website_url} onChange={(e) => setNewP({ ...newP, website_url: e.target.value })}
            placeholder="Website URL — https://example.org"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
            data-testid="new-partner-url" />
          <input value={newP.logo_url} onChange={(e) => setNewP({ ...newP, logo_url: e.target.value })}
            placeholder="Logo URL (optional — paste a public PNG/SVG/JPG, or save then upload a file)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
            data-testid="new-partner-logo" />
          <textarea value={newP.description} onChange={(e) => setNewP({ ...newP, description: e.target.value })}
            placeholder="Short description (optional)"
            rows={2}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-300 focus:outline-none focus:border-emerald-500"
            data-testid="new-partner-desc" />
          <div className="flex items-center gap-3">
            <label className="text-[10px] text-stone-500 uppercase tracking-wider">Sort</label>
            <input type="number" value={newP.sort_order}
              onChange={(e) => setNewP({ ...newP, sort_order: parseInt(e.target.value) || 0 })}
              className="w-20 bg-stone-800 border border-stone-700 rounded-lg px-2 py-1 text-xs text-white" />
            <label className="text-[10px] text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
              <input type="checkbox" checked={newP.is_active}
                onChange={(e) => setNewP({ ...newP, is_active: e.target.checked })} />
              Active (visible on home page)
            </label>
            <div className="flex-1" />
            <button onClick={create}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 rounded-lg flex items-center gap-1"
              data-testid="create-partner-submit">
              <FloppyDisk size={12} /> Add Partner
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading && <p className="text-stone-500 text-sm">Loading partners...</p>}
        {!loading && partners.length === 0 && (
          <div className="bg-stone-900/40 rounded-xl border border-stone-800/60 p-8 text-center">
            <Handshake size={32} className="text-stone-600 mx-auto mb-2" />
            <p className="text-sm text-stone-400">No partners yet. Click <span className="text-emerald-400">Add Partner</span> to create the first one.</p>
          </div>
        )}
        {partners.map((p) => (
          <PartnerRow key={p.id} partner={p}
            onUpdate={update} onDelete={remove} onUploadLogo={uploadLogo} />
        ))}
      </div>
    </div>
  );
}
